<template>
    <div class="patient-share-welcome">
        <div v-if="hasLogo" class="clinic-logo-container">
            <img v-if="clinicDetail.logoUrl" :src="clinicDetail.logoUrl" alt="clinic logo" />
        </div>

        <div class="intro">
            <p v-if="isAuthenticated && user" class="pink-p">
                {{ $translate('hello') }} {{ user.name }}
            </p>
            <p>{{ $translate('welcome_to_embryo_viewer') }}</p>
            <p>{{ $translate('here_you_can_follow_embryo_dev') }}</p>
            <p class="pink-p">{{ $translate('anytime_anywhere') }}</p>
        </div>

        <div v-if="!isAuthenticated" class="login-form">
            <p v-if="authError" class="error-message">{{ authError }}</p>
            <b-button class="pink-btn" @click="login">{{ $translate('login') }}</b-button>
        </div>

        <form v-if="isAuthenticated" class="dob-form" @submit.stop.prevent="goToTreatmentPage">
            <p v-if="error" class="error-message">{{ error }}</p>
            <p v-if="!showBirthYearError" class="pink-p">
                {{ $translate('enter_birth_year') }}
            </p>

            <b-input
                v-model.number="birthYear"
                type="text"
                :class="{ 'is-invalid': showBirthYearError && (!isBirthYearValid || error) }"
                :placeholder="$translate('year_of_birth')"
            />

            <b-button type="submit" class="pink-btn">
                {{ $translate('next') }}
            </b-button>
        </form>

        <div class="footer">
            <span>{{ $translate('powered_by') }}</span>
            <img class="header-logo" src="/static/svg/chloe_logo.svg" alt="" />
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'
import { PATIENT_SHARE_ERRORS } from '../../../business/const'

export default {
    name: 'PatientShareWelcome',
    data: () => ({
        birthYear: null,
        showBirthYearError: false,
        error: null
    }),
    computed: {
        ...mapState({
            treatment: state => state.patient.treatment,
            clinicDetail: state => state.patient.clinicDetail
        }),
        hasLogo() {
            return !!this.clinicDetail.logoUrl
        },
        isBirthYearValid() {
            return !isNaN(this.birthYear) && this.birthYear > 1000 && this.birthYear < 9999
        },
        isAuthenticated() {
            return this.$auth0.isAuthenticated
        },
        user() {
            return this.$auth0.user
        },
        authError() {
            return this.$auth0.error
        }
    },
    async mounted() {
        await this.loadTenantData()
        this.setLocale()

        // hide zendest widget
        zE('webWidget', 'hide')
    },
    methods: {
        async login() {
            try {
                await this.$auth0.login()
            } catch (e) {}
        },
        async goToTreatmentPage() {
            this.showBirthYearError = true
            if (this.isAuthenticated && this.isBirthYearValid) {
                UIUtils.showLoadingMask()
                try {
                    const { cipher } = this.$route.query
                    const token = await this.$auth0.getIdToken()
                    const treatment = await BackendProvider.getSharedToPatientTreatment(
                        {
                            cipher: encodeURIComponent(cipher),
                            birthYear: this.birthYear
                        },
                        token
                    )
                    this.$store.commit('patient/treatment', treatment)
                    this.$store.commit('patient/birthYear', this.birthYear)
                    await this.$router.push({
                        path: '/patient-share/treatment',
                        query: this.$route.query
                    })
                } catch (e) {
                    if (e.body && e.body.message === PATIENT_SHARE_ERRORS.LINK_IS_NOT_VALID) {
                        this.error = this.$translate('email_invalid')
                        setTimeout(() => {
                            this.logoutUser()
                        }, 1000)
                    } else if (
                        e.body &&
                        e.body.message === PATIENT_SHARE_ERRORS.BIRTH_YEAR_MISMATCH
                    ) {
                        this.error = this.$translate('birth_year_error')
                    } else {
                        this.error = this.$translate('server_error')
                    }
                } finally {
                    UIUtils.hideLoadingMask()
                }
            }
        },
        async logoutUser() {
            this.$auth0.logout({ returnTo: window.location.href })
        },
        async loadTenantData() {
            try {
                const { cipher } = this.$route.query
                const clinicDetail = await BackendProvider.fetchPatientSharedTenant(
                    encodeURIComponent(cipher)
                )
                this.$store.commit('patient/clinicDetail', clinicDetail)
            } catch (e) {}
        },
        setLocale() {
            const { language } = this.clinicDetail

            if (language) {
                this.$store.commit('locale/setLocale', language || 'en')
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.patient-share-welcome {
    position: relative;
    height: 100%;
    padding: 3.125rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    .clinic-logo-container {
        width: 175px;
        height: 150px;
        position: relative;
        display: flex;

        img {
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
        }
    }

    .intro,
    .footer {
        display: flex;
        flex-direction: column;
    }
    .error-message {
        margin: 0;
        color: $red;
    }
    .intro {
        font-size: 1.75rem;
        font-weight: 500;

        p {
            margin-bottom: unset;
        }

        @media screen and #{$new-break2close} {
            align-items: flex-start;
        }
        @media screen and #{$new-break3open} {
            align-items: center;
        }
    }

    p.pink-p {
        color: $pink;
        font-weight: 800;
        margin-bottom: 1rem;
    }

    .login-form {
        display: grid;
        align-items: center;
        width: 10rem;
    }

    .dob-form {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 22rem;

        .pink-btn {
            margin-top: 1rem;
        }
    }

    .btn.btn-secondary.pink-btn {
        padding: 0.625rem 1.25rem;
        font-size: 1.25rem !important;
        font-weight: 600 !important;
        color: $light-3;
        background: $pink-gradient !important;

        @media screen and #{$new-break2close} {
            width: 100%;
        }
    }

    .footer {
        align-items: center;

        img {
            margin: 0.625rem 0;
        }
    }
}
</style>
